import React, { useState, useEffect, useRef } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import './RowActionsMenu.css';

/**
 * RowActionsMenu — list-row action cluster with an overflow menu.
 *
 * `actions` (array, in display order):
 *   - primary: true      → always rendered as a visible icon button
 *   - show: false        → omitted (status-gated callers just pass show)
 *   - label (required)   → accessible name + tooltip + menu text
 *   - icon               → react-icons component
 *   - onClick            → handler (menu closes first)
 *   - className          → extra button classes, e.g. btn-danger
 */
const RowActionsMenu = ({ actions = [], overflowIcon = FiMoreVertical }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const OverflowIcon = overflowIcon;
  const visible = actions.filter((a) => a.show !== false && a.primary);
  const overflow = actions.filter((a) => a.show !== false && !a.primary);

  return (
    <div className="row-actions" ref={containerRef}>
      {visible.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.label}
            type="button"
            className={`btn btn-sm ${action.className || 'btn-secondary'}`}
            title={action.label}
            aria-label={action.label}
            onClick={action.onClick}
          >
            {Icon ? <Icon /> : action.label}
          </button>
        );
      })}
      {overflow.length > 0 && (
        <div className="row-actions-menu">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            title="More actions"
            aria-label="More actions"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            <OverflowIcon />
          </button>
          {open && (
            <div className="dropdown-menu show row-actions-dropdown">
              {overflow.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.label}
                    type="button"
                    className={`dropdown-item ${action.danger ? 'danger' : ''}`}
                    onClick={() => {
                      setOpen(false);
                      action.onClick();
                    }}
                  >
                    {Icon && <Icon style={{ width: 14, height: 14, marginRight: 8 }} />}
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
